'use client'

import { useEffect, useState, useRef, useCallback } from 'react'
import Link from 'next/link'
import { FglHeader } from '@/components/layout/fgl-header'
import { GliderBackdrop, GliderSwarm, LivingThermal, Reveal } from '@/components/award'
import { useTranslation } from '@/i18n/client'
import { Users, Mail, Phone, Globe, MapPin, Search, X } from 'lucide-react'

interface Contact {
  id: string
  name: string
  role: string
  region: string
  bio: string | null
  photo_url: string | null
  email: string
  phone: string | null
  website: string | null
  lat: number
  lon: number
}

// HTML-Escaping gegen XSS in Leaflet-Popups
function esc(text: string): string {
  const d = document.createElement('div')
  d.textContent = text
  return d.innerHTML
}

// URL-Validierung: https/http oder lokaler Pfad (/contacts/..., /images/...) erlaubt.
// Keine protocol-relativen URLs (//…) und keine javascript:-URLs.
function isSafeUrl(url: string | null): boolean {
  if (!url) return false
  // Lokaler Pfad auf eigene Origin
  if (url.startsWith('/') && !url.startsWith('//')) return true
  try {
    const parsed = new URL(url)
    return parsed.protocol === 'https:' || parsed.protocol === 'http:'
  } catch { return false }
}

// CSS einmalig nachladen (Leaflet + MarkerCluster)
function ensureLeafletAssets() {
  if (typeof document === 'undefined') return
  const urls = [
    'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css',
    'https://unpkg.com/leaflet.markercluster@1.5.3/dist/MarkerCluster.css',
  ]
  for (const href of urls) {
    if (!document.querySelector(`link[href="${href}"]`)) {
      const link = document.createElement('link')
      link.rel = 'stylesheet'
      link.href = href
      document.head.appendChild(link)
    }
  }
  // Eigene Cluster- und Marker-Styles (FGL Gold, Glassmorphism)
  if (!document.getElementById('fgl-contacts-map-styles')) {
    const style = document.createElement('style')
    style.id = 'fgl-contacts-map-styles'
    style.textContent = `
      .fgl-contact-marker { background: transparent !important; border: 0 !important; }
      .fgl-contact-marker .dot {
        width: 44px; height: 44px; border-radius: 50%;
        border: 2.5px solid #f2c94e;
        box-shadow: 0 6px 18px -4px rgba(0,0,0,.55), 0 0 0 3px rgba(242,201,78,.12);
        background-color: #0a0a0a;
        background-size: cover; background-position: center;
        display: flex; align-items: center; justify-content: center;
        color: #f2c94e; font-weight: 900; font-size: 14px;
        transition: transform .18s ease, box-shadow .18s ease;
        cursor: pointer;
      }
      .fgl-contact-marker .dot:hover {
        transform: translateY(-2px) scale(1.06);
        box-shadow: 0 10px 24px -4px rgba(0,0,0,.65), 0 0 0 4px rgba(242,201,78,.22);
      }
      .fgl-contact-marker.is-selected .dot {
        box-shadow: 0 10px 28px -4px rgba(0,0,0,.7), 0 0 0 6px rgba(242,201,78,.35);
        transform: scale(1.08);
      }

      /* Cluster: schwarzes Glass-Chip mit Gold-Ring */
      .fgl-cluster { background: transparent !important; border: 0 !important; }
      .fgl-cluster .core {
        position: relative;
        border-radius: 999px;
        background: rgba(10,10,10,.82);
        backdrop-filter: blur(10px);
        -webkit-backdrop-filter: blur(10px);
        border: 2px solid rgba(242,201,78,.75);
        color: #f2c94e;
        font-weight: 900;
        letter-spacing: .02em;
        display: flex; align-items: center; justify-content: center;
        box-shadow:
          0 10px 30px -10px rgba(0,0,0,.75),
          0 0 0 4px rgba(242,201,78,.10),
          inset 0 0 20px rgba(242,201,78,.08);
        transition: transform .18s ease, box-shadow .18s ease, border-color .18s ease;
      }
      .fgl-cluster .core:hover {
        transform: translateY(-2px) scale(1.05);
        border-color: rgba(242,201,78,1);
        box-shadow:
          0 14px 36px -10px rgba(0,0,0,.85),
          0 0 0 6px rgba(242,201,78,.18),
          inset 0 0 24px rgba(242,201,78,.14);
      }
      .fgl-cluster .pulse {
        position: absolute; inset: -6px;
        border-radius: 999px;
        border: 2px solid rgba(242,201,78,.35);
        animation: fgl-cluster-pulse 2.4s ease-out infinite;
        pointer-events: none;
      }
      @keyframes fgl-cluster-pulse {
        0%   { transform: scale(.9);  opacity: .55; }
        80%  { transform: scale(1.25); opacity: 0; }
        100% { transform: scale(1.25); opacity: 0; }
      }

      /* Leaflet-Controls dezent an Theme angleichen */
      .leaflet-control-zoom a {
        background: rgba(10,10,10,.85) !important;
        color: #f2c94e !important;
        border: 1px solid rgba(242,201,78,.35) !important;
        backdrop-filter: blur(8px);
      }
      .leaflet-control-zoom a:hover { background: rgba(242,201,78,.15) !important; }

      /* Popup: dunkel + Gold-Akzent */
      .leaflet-popup-content-wrapper {
        background: rgba(12,12,18,.96) !important;
        color: #eaeaea !important;
        border: 1px solid rgba(242,201,78,.30) !important;
        border-radius: 16px !important;
        box-shadow: 0 20px 50px -10px rgba(0,0,0,.8) !important;
        backdrop-filter: blur(10px);
      }
      .leaflet-popup-tip { background: rgba(12,12,18,.96) !important; }
      .leaflet-popup-content { margin: 14px 16px !important; }
      /* Schoener Close-Button: Goldener Kreis statt nackter X-Glyphe */
      .leaflet-popup-close-button {
        position: absolute !important;
        top: 8px !important;
        right: 8px !important;
        width: 28px !important;
        height: 28px !important;
        padding: 0 !important;
        line-height: 26px !important;
        text-align: center !important;
        font-size: 16px !important;
        font-weight: 700 !important;
        color: #f2c94e !important;
        background: rgba(242,201,78,.10) !important;
        border: 1px solid rgba(242,201,78,.35) !important;
        border-radius: 50% !important;
        backdrop-filter: blur(4px);
        transition: all .2s ease !important;
      }
      .leaflet-popup-close-button:hover {
        background: rgba(242,201,78,.25) !important;
        border-color: rgba(242,201,78,.7) !important;
        transform: scale(1.08);
      }
      .leaflet-popup-close-button:focus-visible {
        outline: 2px solid #f2c94e !important;
        outline-offset: 2px !important;
      }
    `
    document.head.appendChild(style)
  }
}

/**
 * Globale Reveal-Funktion für DSGVO-konforme E-Mail/Telefon-Anzeige.
 * Erst nach User-Click wird der Wert decodiert + sichtbar — Bots ohne JS sehen
 * nur Base64-Strings, Bots mit JS aber ohne Click triggern den Reveal nicht.
 * Wird einmal global registriert + per onclick im Popup-HTML referenziert.
 */
function ensureRevealFunction() {
  if (typeof window === 'undefined') return
  if ((window as any).__fglReveal) return
  ;(window as any).__fglReveal = (btn: HTMLElement, kind: 'mail' | 'tel', b64: string, ev?: Event) => {
    try {
      // Verhindere, dass Leaflet die Popup-Schliessen-Logik triggert
      if (ev) { ev.stopPropagation(); ev.preventDefault?.() }
      const decoded = decodeURIComponent(escape(atob(b64)))
      const safe = decoded.replace(/[<>"']/g, (m) => ({ '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[m] as string))
      const href = (kind === 'mail' ? 'mailto:' : 'tel:') + decoded
      const icon = kind === 'mail' ? '✉' : '☎'
      btn.outerHTML = `<a href="${href}" onclick="event.stopPropagation()" style="color:${kind === 'mail' ? '#8ec5ff' : '#eaeaea'};text-decoration:none;font-weight:600;">${icon} ${safe}</a>`
    } catch { /* malformed b64 */ }
  }
}

// Leaflet-Karte mit Marker-Clustering
function ContactsMap({ contacts, selectedId, onSelect, focusRegion, allContacts, viewerAuth }: {
  contacts: Contact[]
  selectedId: string | null
  onSelect: (id: string) => void
  focusRegion: string
  allContacts: Contact[]
  viewerAuth: boolean
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<any>(null)
  const clusterRef = useRef<any>(null)
  const markersRef = useRef<any[]>([])
  const leafletRef = useRef<any>(null)

  const initMap = useCallback(async () => {
    if (!containerRef.current || mapRef.current) return
    ensureLeafletAssets()
    ensureRevealFunction()
    const leafletMod: any = await import('leaflet')
    // Leaflet hat sowohl named als auch default export — das Plugin hängt sich
    // an den default export (die Plugin-Ausführung nutzt intern `L.Marker.extend`).
    const L: any = leafletMod.default || leafletMod
    // Damit das Plugin das global bekannte Leaflet findet, hängen wir es an window.
    if (typeof window !== 'undefined' && !(window as any).L) {
      (window as any).L = L
    }
    await import('leaflet.markercluster')
    leafletRef.current = L

    if ((containerRef.current as any)?._leaflet_id) {
      delete (containerRef.current as any)._leaflet_id
    }

    const map = L.map(containerRef.current!, {
      center: [51.1657, 10.4515],
      zoom: 5,
      minZoom: 3,
      maxZoom: 18,
      zoomControl: true,
      attributionControl: true,
      worldCopyJump: true,
      preferCanvas: false,
      zoomSnap: 0.5,
      wheelPxPerZoomLevel: 90,
    })

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19,
      subdomains: ['a', 'b', 'c'],
    }).addTo(map)

    // Cluster-Gruppe
    const cluster = (L as any).markerClusterGroup({
      showCoverageOnHover: false,
      spiderfyOnMaxZoom: true,
      spiderfyDistanceMultiplier: 1.6,
      disableClusteringAtZoom: 12,
      maxClusterRadius: (zoom: number) => {
        // bei niedrigem Zoom weite Cluster, bei hohem enger
        if (zoom <= 4) return 90
        if (zoom <= 6) return 70
        if (zoom <= 8) return 55
        if (zoom <= 10) return 40
        return 30
      },
      iconCreateFunction: (c: any) => {
        const n = c.getChildCount()
        // Größen-Tier
        const tier = n < 5 ? { size: 42, font: 14 } : n < 15 ? { size: 54, font: 16 } : { size: 66, font: 18 }
        return L.divIcon({
          className: 'fgl-cluster',
          html: `
            <div class="core" style="width:${tier.size}px;height:${tier.size}px;font-size:${tier.font}px;">
              <span class="pulse"></span>
              <span>${n}</span>
            </div>
          `,
          iconSize: [tier.size, tier.size],
          iconAnchor: [tier.size / 2, tier.size / 2],
        })
      },
    })
    clusterRef.current = cluster
    map.addLayer(cluster)

    mapRef.current = map
    updateMarkers(contacts, selectedId)
  }, [])

  useEffect(() => {
    initMap()
    return () => {
      if (mapRef.current) {
        mapRef.current.remove()
        mapRef.current = null
        clusterRef.current = null
      }
    }

  }, [])

  useEffect(() => {
    if (!mapRef.current) return
    updateMarkers(contacts, selectedId)
    // 2026-06-21: viewerAuth muss als Dep mit, sonst zeigt das Map-Popup
    // weiter Reveal-Buttons fuer eingeloggte Sicht obwohl viewerAuth=false
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contacts, viewerAuth])

  useEffect(() => {
    if (!mapRef.current || !clusterRef.current || !selectedId) return
    const c = contacts.find(c => c.id === selectedId)
    if (!c) return
    const marker = markersRef.current.find((m: any) => m._contactId === selectedId)
    if (marker) {
      // zoomToShowLayer sorgt dafür, dass aus einem Cluster rausgezoomed wird
      clusterRef.current.zoomToShowLayer(marker, () => {
        marker.openPopup()
      })
    } else {
      mapRef.current.flyTo([c.lat, c.lon], 11, { duration: 0.8 })
    }
    // CSS-Klasse für Hervorhebung neu setzen
    markersRef.current.forEach((m: any) => {
      const el = m.getElement?.()
      if (el) el.classList.toggle('is-selected', m._contactId === selectedId)
    })
  }, [selectedId, contacts])

  // Region-Pill geklickt → Karte auf Region-Bounds zoomen (oder global wenn leer)
  const initialFocusRef = useRef(true)
  useEffect(() => {
    if (!mapRef.current || !leafletRef.current) return
    // Beim ersten Render nicht flyen — der initiale Map-Setup hat bereits einen Center
    if (initialFocusRef.current) {
      initialFocusRef.current = false
      return
    }
    const L = leafletRef.current
    if (!focusRegion) {
      // Reset auf Welt-Übersicht
      mapRef.current.flyTo([51.1657, 10.4515], 5, { duration: 0.8 })
      return
    }
    const inRegion = allContacts.filter(c => c.region === focusRegion)
    if (inRegion.length === 0) return
    if (inRegion.length === 1) {
      mapRef.current.flyTo([inRegion[0].lat, inRegion[0].lon], 9, { duration: 0.8 })
      return
    }
    const bounds = L.latLngBounds(inRegion.map(c => [c.lat, c.lon]))
    mapRef.current.flyToBounds(bounds, { duration: 0.8, padding: [60, 60], maxZoom: 10 })
  }, [focusRegion, allContacts])

  function updateMarkers(list: Contact[], activeId: string | null) {
    const L = leafletRef.current
    const cluster = clusterRef.current
    if (!L || !cluster) return
    cluster.clearLayers()
    markersRef.current = []

    list.forEach((c) => {
      const initials = c.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
      const hasPhoto = isSafeUrl(c.photo_url)
      const safePhotoUrl = hasPhoto ? encodeURI(c.photo_url!) : ''
      const safeInitials = esc(initials)

      const bg = hasPhoto
        ? `background-image:url('${safePhotoUrl}');`
        : ``

      const icon = L.divIcon({
        className: 'fgl-contact-marker' + (c.id === activeId ? ' is-selected' : ''),
        html: `<div class="dot" style="${bg}">${hasPhoto ? '' : safeInitials}</div>`,
        iconSize: [44, 44],
        iconAnchor: [22, 22],
        popupAnchor: [0, -26],
      })

      const safeName = esc(c.name)
      const safeRole = esc(c.role)
      const safeRegion = esc(c.region)
      // DSGVO + Bot-Schutz: Email/Telefon als Base64 in HTML — Plaintext nur
      // nach explizitem User-Click sichtbar (JS-decodiert). Bots ohne JS sehen
      // nur kryptische Strings, Bots ohne Click triggern den Reveal nicht.
      const b64Email = c.email ? btoa(unescape(encodeURIComponent(c.email))) : ''
      const b64Phone = c.phone ? btoa(unescape(encodeURIComponent(c.phone))) : ''

      const avatar = hasPhoto
        ? `<img src="${safePhotoUrl}" alt="" style="width:44px;height:44px;border-radius:50%;object-fit:cover;border:2px solid #f2c94e;" referrerpolicy="no-referrer" />`
        : `<div style="width:44px;height:44px;border-radius:50%;background:#151520;border:2px solid #f2c94e;display:flex;align-items:center;justify-content:center;color:#f2c94e;font-weight:900;font-size:15px;">${safeInitials}</div>`

      const popup = `
        <div style="min-width:220px;max-width:280px;font-family:Inter,system-ui,sans-serif;">
          <div style="display:flex;align-items:center;gap:12px;margin-bottom:10px;">
            ${avatar}
            <div style="min-width:0;">
              <div style="font-weight:900;font-size:14px;color:#f7f7f7;line-height:1.2;">${safeName}</div>
              <div style="font-size:11px;color:#9a9a9a;margin-top:2px;">${safeRole}</div>
            </div>
          </div>
          <div style="margin-bottom:10px;">
            <span style="display:inline-flex;align-items:center;gap:4px;padding:3px 10px;border-radius:999px;background:rgba(242,201,78,.12);color:#f2c94e;font-weight:800;font-size:10px;letter-spacing:.04em;text-transform:uppercase;border:1px solid rgba(242,201,78,.28);">${safeRegion}</span>
          </div>
          <div style="display:flex;flex-direction:column;gap:6px;font-size:12px;">
            ${viewerAuth
              ? `${b64Email ? `<button type="button" onclick="event.stopPropagation();event.preventDefault();window.__fglReveal&&window.__fglReveal(this,'mail','${b64Email}',event);return false;" style="all:unset;cursor:pointer;color:#8ec5ff;font-weight:600;display:inline-flex;align-items:center;gap:6px;">✉ E-Mail anzeigen</button>` : ''}
                 ${b64Phone ? `<button type="button" onclick="event.stopPropagation();event.preventDefault();window.__fglReveal&&window.__fglReveal(this,'tel','${b64Phone}',event);return false;" style="all:unset;cursor:pointer;color:#eaeaea;font-weight:600;display:inline-flex;align-items:center;gap:6px;">☎ Telefon anzeigen</button>` : ''}`
              : `<a href="/login?next=/contacts" style="display:inline-flex;align-items:center;gap:6px;padding:6px 10px;border-radius:8px;background:rgba(242,201,78,.12);border:1px solid rgba(242,201,78,.40);color:#f2c94e;font-weight:800;font-size:11px;text-decoration:none;">🔒 Einloggen, um Kontakt zu sehen</a>`
            }
          </div>
        </div>
      `

      const marker = L.marker([c.lat, c.lon], { icon, riseOnHover: true })
      marker.bindPopup(popup, { closeButton: true, autoPan: true, offset: [0, -4] })
      marker._contactId = c.id
      marker.on('click', () => onSelect(c.id))
      markersRef.current.push(marker)
      cluster.addLayer(marker)
    })
  }

  return (
    <div ref={containerRef} className="w-full h-full rounded-[20px] overflow-hidden" style={{ minHeight: 400 }} />
  )
}

// FGL-Regionen fuer Filter
const FGL_REGIONS = [
  'Norddeutschland', 'Niedersachsen', 'Schleswig-Holstein', 'Nordrhein-Westfalen',
  'Hessen', 'Rheinland-Pfalz', 'Saarland', 'Baden-Wuerttemberg', 'Bayern',
  'Sachsen', 'Thueringen', 'Brandenburg', 'Mecklenburg-Vorpommern',
  'Oesterreich', 'Schweiz', 'International',
]

// Region → Land/Kontinent Map. Bestand der Plattform stand 2026-05-09 hat
// noch kein eigenes country-Feld in FglContact; diese Map leitet das Land
// pragmatisch aus dem region-String ab. Zukuenftige Migration setzt country
// direkt auf den Datensatz und macht diese Map ueberfluessig (Phase 2).
const REGION_TO_COUNTRY: Record<string, string> = {
  // Deutschland (16 Bundeslaender)
  'Baden-Württemberg': 'Deutschland', 'Bayern': 'Deutschland', 'Berlin': 'Deutschland',
  'Brandenburg': 'Deutschland', 'Bremen': 'Deutschland', 'Hamburg': 'Deutschland',
  'Hessen': 'Deutschland', 'Mecklenburg-Vorpommern': 'Deutschland', 'Niedersachsen': 'Deutschland',
  'Nordrhein-Westfalen': 'Deutschland', 'Rheinland-Pfalz': 'Deutschland', 'Saarland': 'Deutschland',
  'Sachsen': 'Deutschland', 'Sachsen-Anhalt': 'Deutschland', 'Schleswig-Holstein': 'Deutschland',
  'Thüringen': 'Deutschland', 'Norddeutschland': 'Deutschland',
  // Österreich (9 Bundeslaender)
  'Burgenland': 'Österreich', 'Kärnten': 'Österreich', 'Niederösterreich': 'Österreich',
  'Oberösterreich': 'Österreich', 'Salzburg': 'Österreich', 'Steiermark': 'Österreich',
  'Tirol': 'Österreich', 'Vorarlberg': 'Österreich', 'Wien': 'Österreich',
  // Schweiz (Kantone — populaere)
  'Zürich': 'Schweiz', 'Bern': 'Schweiz', 'Luzern': 'Schweiz', 'Uri': 'Schweiz',
  'Schwyz': 'Schweiz', 'Obwalden': 'Schweiz', 'Nidwalden': 'Schweiz', 'Glarus': 'Schweiz',
  'Zug': 'Schweiz', 'Freiburg': 'Schweiz', 'Solothurn': 'Schweiz', 'Basel-Stadt': 'Schweiz',
  'Basel-Landschaft': 'Schweiz', 'Schaffhausen': 'Schweiz', 'Appenzell': 'Schweiz',
  'St. Gallen': 'Schweiz', 'Graubünden': 'Schweiz', 'Aargau': 'Schweiz', 'Thurgau': 'Schweiz',
  'Tessin': 'Schweiz', 'Waadt': 'Schweiz', 'Wallis': 'Schweiz', 'Neuenburg': 'Schweiz',
  'Genf': 'Schweiz', 'Jura': 'Schweiz',
  // Self-mapping fuer Laender, die der User direkt eingibt
  'Deutschland': 'Deutschland', 'Österreich': 'Österreich', 'Oesterreich': 'Österreich',
  'Schweiz': 'Schweiz', 'Italien': 'Italien', 'Frankreich': 'Frankreich',
  'England': 'Vereinigtes Königreich', 'Schottland': 'Vereinigtes Königreich',
  'Wales': 'Vereinigtes Königreich', 'Nordirland': 'Vereinigtes Königreich',
  'Vereinigtes Königreich': 'Vereinigtes Königreich', 'UK': 'Vereinigtes Königreich',
  'Niederlande': 'Niederlande', 'Belgien': 'Belgien', 'Luxemburg': 'Luxemburg',
  'Spanien': 'Spanien', 'Portugal': 'Portugal', 'Polen': 'Polen', 'Tschechien': 'Tschechien',
  'Slowakei': 'Slowakei', 'Slowenien': 'Slowenien', 'Ungarn': 'Ungarn',
  'Schweden': 'Schweden', 'Norwegen': 'Norwegen', 'Finnland': 'Finnland', 'Dänemark': 'Dänemark',
  'Australien': 'Australien', 'Neuseeland': 'Neuseeland',
  'USA': 'USA', 'Kanada': 'Kanada',
  'International': 'International',
}

function regionToCountry(region: string): string {
  if (REGION_TO_COUNTRY[region]) return REGION_TO_COUNTRY[region]
  // Unbekannte Region: nutze sie selbst als Land — so fallen Einzeleintraege
  // wie „Tasmanien" eigene Pille statt unter „International" zu landen.
  return region
}

export default function ContactsPage() {
  const { t } = useTranslation()
  const [contacts, setContacts] = useState<Contact[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [countryFilter, setCountryFilter] = useState<string>('')
  const [regionFilter, setRegionFilter] = useState<string>('')
  const [searchQuery, setSearchQuery] = useState('')
  // 2026-06-21 Stephan-Spec: Email + Telefon NUR fuer eingeloggte User.
  // API liefert viewerAuth=true|false → UI zeigt Reveal-Buttons oder
  // "Bitte einloggen"-Hinweis statt Klartext-Reveal.
  const [viewerAuth, setViewerAuth] = useState<boolean>(false)

  useEffect(() => {
    fetch('/api/contacts')
      .then(r => r.json())
      .then(data => {
        if (data.success) {
          setContacts(data.data)
          setViewerAuth(!!data.viewerAuth)
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  // Wenn Country gewechselt wird, regionFilter zuruecksetzen
  useEffect(() => {
    setRegionFilter('')
  }, [countryFilter])

  // Laender aus Kontakten ableiten (sortiert)
  const countries = Array.from(new Set(contacts.map(c => regionToCountry(c.region)))).sort((a, b) => a.localeCompare(b, 'de'))

  // Regionen pro Land — wenn ein Land ausgewaehlt ist, zeige nur dessen
  // Sub-Regionen. Wenn das Land nur EINE Region hat (z.B. Italien gesamt),
  // verstecken wir die Region-Pille (kein zusaetzlicher Zwischenfilter noetig).
  const regionsForCountry = countryFilter
    ? Array.from(new Set(contacts.filter(c => regionToCountry(c.region) === countryFilter).map(c => c.region))).sort((a, b) => a.localeCompare(b, 'de'))
    : []
  const showRegionPills = regionsForCountry.length > 1

  // Nachname extrahieren (letzter Wort-Token; kompositionssicher fuer Doppelnamen).
  // Fallback: voller Name.
  const surnameOf = (fullName: string): string => {
    const tokens = fullName.trim().split(/\s+/)
    return tokens.length > 1 ? tokens[tokens.length - 1] : fullName
  }

  // Filtern (Country gewinnt; Region innerhalb Country) + nach Nachname sortieren
  const filtered = contacts.filter(c => {
    const country = regionToCountry(c.region)
    const matchCountry = !countryFilter || country === countryFilter
    const matchRegion = !regionFilter || c.region === regionFilter
    const matchSearch = !searchQuery || c.name.toLowerCase().includes(searchQuery.toLowerCase()) || c.role.toLowerCase().includes(searchQuery.toLowerCase())
    return matchCountry && matchRegion && matchSearch
  }).sort((a, b) => surnameOf(a.name).localeCompare(surnameOf(b.name), 'de', { sensitivity: 'base' }))

  const getInitials = (name: string) => name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()

  return (
    <div data-award="true" className="relative min-h-screen overflow-x-clip" style={{ background: 'var(--bg-primary)' }}>
      <FglHeader />
      <GliderBackdrop />
      <div className="relative z-10">
        {/* HERO — Award-Pattern wie auf Features/FAQ/Donate */}
        <section className="relative pt-[100px] pb-8 md:pt-[120px] md:pb-10 px-4 sm:px-6 lg:px-8 overflow-hidden">
          <LivingThermal />
          <div className="absolute inset-0 award-noise pointer-events-none" />
          <div className="relative z-10 max-w-[1400px] mx-auto">
            <Reveal>
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <span className="award-eyebrow">{t('contacts.eyebrow')}</span>
                <span className="h-px w-12" style={{ background: 'var(--award-line-hi)' }} />
                <span className="award-label">{contacts.length > 0 ? t('contacts.contactsCount', { n: contacts.length }) : t('contacts.regionalPersonal')}</span>
              </div>
            </Reveal>

            <div className="grid grid-cols-12 gap-4 md:gap-10 items-end">
              <div className="col-span-12 md:col-span-7">
                <Reveal delay={1}>
                  <h1 className="award-h-mega" style={{ marginBottom: 0 }}>
                    {t('contacts.heroTitlePrefix')} <span className="award-grad-text">{t('contacts.heroTitleAccent')}</span>
                  </h1>
                </Reveal>
              </div>
              <div className="col-span-12 md:col-span-5">
                <Reveal delay={2}>
                  <p className="text-base md:text-lg leading-relaxed" style={{ color: 'var(--award-cirrus-2)' }}>
                    {t('contacts.heroLead')}
                  </p>
                  <div className="mt-5">
                    <Link
                      href="/contacts/apply"
                      className="btn-primary !px-5 !py-3 !text-[13px] font-black tracking-[.06em] uppercase inline-block"
                      data-tip={t('contacts.becomeContactTip')}
                    >
                      {t('contacts.becomeContactCta')}
                    </Link>
                  </div>
                </Reveal>
              </div>
            </div>
          </div>
        </section>

        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 pb-8">

          {/* Filter + Search — Hierarchisch: Land-Dropdown (Parent) + Region-Pills (Children) */}
          <div className="flex flex-col gap-3 mb-6">
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Search */}
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--muted)]" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('contacts.searchPlaceholder')}
                  className="input !ps-10 !py-2.5 !text-[13px]"
                  data-tip={t('contacts.searchTip')}
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute end-3 top-1/2 -translate-y-1/2 text-[var(--muted)] hover:text-[var(--text)]"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Land-Dropdown (Parent) */}
              <div className="relative">
                <select
                  value={countryFilter}
                  onChange={(e) => setCountryFilter(e.target.value)}
                  className="input !py-2.5 !text-[13px] !appearance-auto pe-10"
                  data-tip={t('contacts.countryFilterTip')}
                  style={{ minWidth: '220px' }}
                >
                  <option value="">{t('contacts.countryFilterAll')}</option>
                  {countries.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Region-Pills (Children, nur sichtbar wenn ein Land mit mehreren Regionen aktiv) */}
            {showRegionPills && (
              <div className="flex gap-2 flex-wrap items-center">
                <span className="text-[10px] font-bold tracking-[.10em] uppercase me-1" style={{ color: 'var(--muted)' }}>
                  {t('contacts.regionLabel')}
                </span>
                <button
                  onClick={() => setRegionFilter('')}
                  className={`px-3 py-1.5 rounded-full text-[11px] font-bold tracking-[.04em] border transition-colors ${
                    !regionFilter
                      ? 'bg-[var(--accent)]/15 text-[var(--accent)] border-[var(--accent)]/30'
                      : 'bg-[var(--pill-bg)] text-[var(--muted)] border-[var(--border)] hover:border-[var(--accent)]/30'
                  }`}
                  data-tip={t('contacts.allRegionsTip', { country: countryFilter })}
                >
                  {t('contacts.allRegions')}
                </button>
                {regionsForCountry.map((r) => (
                  <button
                    key={r}
                    onClick={() => setRegionFilter(regionFilter === r ? '' : r)}
                    className={`px-3 py-1.5 rounded-full text-[11px] font-bold tracking-[.04em] border transition-colors ${
                      regionFilter === r
                        ? 'bg-[var(--accent)]/15 text-[var(--accent)] border-[var(--accent)]/30'
                        : 'bg-[var(--pill-bg)] text-[var(--muted)] border-[var(--border)] hover:border-[var(--accent)]/30'
                    }`}
                  >
                    {r}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Map */}
          <div className="rounded-[20px] border border-[var(--border)] bg-[var(--bg-card)] overflow-hidden mb-8 relative z-0"
            style={{ height: '450px' }}>
            {loading ? (
              <div className="w-full h-full flex items-center justify-center">
                <div className="text-[var(--muted)] text-[13px] font-bold animate-pulse">{t('contacts.mapLoading')}</div>
              </div>
            ) : (
              <ContactsMap
                contacts={filtered}
                selectedId={selectedId}
                onSelect={setSelectedId}
                focusRegion={regionFilter}
                allContacts={contacts}
                viewerAuth={viewerAuth}
              />
            )}
          </div>

          {/* Contact Cards Grid */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="rounded-[20px] border border-[var(--border)] bg-[var(--bg-card)] p-6 animate-pulse">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-14 h-14 rounded-full bg-[var(--pill-bg)]" />
                    <div className="flex-1">
                      <div className="h-4 bg-[var(--pill-bg)] rounded w-2/3 mb-2" />
                      <div className="h-3 bg-[var(--pill-bg)] rounded w-1/2" />
                    </div>
                  </div>
                  <div className="h-3 bg-[var(--pill-bg)] rounded w-full mb-2" />
                  <div className="h-3 bg-[var(--pill-bg)] rounded w-3/4" />
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16">
              <Users className="w-12 h-12 text-[var(--muted)] mx-auto mb-4 opacity-40" />
              <p className="text-[var(--muted)] text-[14px] font-bold">
                {contacts.length === 0 ? t('contacts.emptyAllContacts') : t('contacts.emptyFiltered')}
              </p>
              {contacts.length === 0 && (
                <Link href="/contacts/apply" className="btn-primary !px-5 !py-2.5 !text-[12px] mt-4 inline-block" data-tip={t('contacts.applyNowTip')}>
                  {t('contacts.applyNow')}
                </Link>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map(c => (
                <div
                  key={c.id}
                  onClick={() => setSelectedId(c.id)}
                  className={`rounded-[20px] border bg-[var(--bg-card)] p-6 cursor-pointer transition-all hover:border-[var(--accent)]/40 hover:shadow-lg ${
                    selectedId === c.id
                      ? 'border-[var(--accent)]/50 shadow-[0_0_20px_rgba(242,201,78,.1)]'
                      : 'border-[var(--border)]'
                  }`}
                >
                  {/* Avatar + Name */}
                  <div className="flex items-center gap-4 mb-4">
                    {isSafeUrl(c.photo_url) ? (
                      <img
                        src={c.photo_url!}
                        alt={c.name}
                        className="w-14 h-14 rounded-full object-cover border-[3px] border-[var(--accent)]"
                        referrerPolicy="no-referrer"
                      />
                    ) : (
                      <div className="w-14 h-14 rounded-full border-[3px] border-[var(--accent)] bg-[var(--pill-bg)] flex items-center justify-center text-[var(--accent)] font-black text-[16px]">
                        {getInitials(c.name)}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-[16px] font-black text-[var(--text)] truncate">{c.name}</h3>
                      <p className="text-[12px] text-[var(--muted)] font-bold truncate">{c.role}</p>
                    </div>
                  </div>

                  {/* Region Badge */}
                  <div className="mb-3">
                    <span className="inline-block px-3 py-1 rounded-full text-[10px] font-bold tracking-[.04em] bg-[var(--accent)]/10 text-[var(--accent)] border border-[var(--accent)]/20">
                      <MapPin className="w-3 h-3 inline me-1 -mt-0.5" />
                      {c.region}
                    </span>
                  </div>

                  {/* Bio */}
                  {c.bio && (
                    <p className="text-[13px] text-[var(--muted)] leading-relaxed mb-4 line-clamp-2">
                      {c.bio}
                    </p>
                  )}

                  {/* Contact Icons — DSGVO: Erst nach Click sichtbar.
                      2026-06-21 Stephan-Spec: Email + Phone nur fuer
                      eingeloggte User. Public-User sehen Login-Hinweis-Pille. */}
                  <div className="flex items-center gap-3 pt-3 border-t border-[var(--border)] flex-wrap">
                    {viewerAuth ? (
                      <>
                        <RevealLink kind="mail" value={c.email} />
                        {c.phone && <RevealLink kind="tel" value={c.phone} />}
                      </>
                    ) : (
                      <Link
                        href="/login?next=/contacts"
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider border"
                        style={{
                          background: 'color-mix(in oklch, var(--accent) 14%, transparent)',
                          borderColor: 'color-mix(in oklch, var(--accent) 45%, transparent)',
                          color: 'var(--accent)',
                        }}
                      >
                        🔒 Einloggen, um Kontakt zu sehen
                      </Link>
                    )}
                    {c.website && isSafeUrl(c.website) && (
                      <a
                        href={c.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="flex items-center gap-1.5 text-[11px] font-bold text-[var(--link-col)] hover:text-[var(--accent)] transition-colors"
                        title={c.website}
                        data-tip={t('contacts.openWebsiteTip')}
                      >
                        <Globe className="w-3.5 h-3.5" />
                        {t('contacts.openWebsite')}
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Footer CTA */}
          {contacts.length > 0 && (
            <div className="text-center mt-12 py-8 border-t border-[var(--border)]">
              <p className="text-[var(--muted)] text-[14px] font-bold mb-4">
                {t('contacts.footerCtaQuestion')}
              </p>
              <Link href="/contacts/apply" className="btn-primary !px-6 !py-3 !text-[13px] font-black tracking-[.06em] uppercase" data-tip={t('contacts.footerCtaTip')}>
                {t('contacts.footerCta')}
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * DSGVO-konformer Reveal-Button: zeigt E-Mail/Telefon erst nach Click an.
 * Bots ohne JS sehen nur das Label, Bots mit JS aber ohne Click triggern keinen
 * Reveal — nur explizite User-Interaktion macht den Plaintext sichtbar.
 */
function RevealLink({ kind, value }: { kind: 'mail' | 'tel'; value: string }) {
  const { t } = useTranslation()
  const [revealed, setRevealed] = useState(false)
  const Icon = kind === 'mail' ? Mail : Phone
  const label = kind === 'mail' ? t('contacts.revealEmail') : t('contacts.revealPhone')

  if (!revealed) {
    return (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          e.preventDefault()
          setRevealed(true)
        }}
        onMouseDown={(e) => e.stopPropagation()}
        className="flex items-center gap-1.5 text-[11px] font-bold text-[var(--link-col)] hover:text-[var(--accent)] transition-colors"
        data-tip={t('contacts.revealTip')}
      >
        <Icon className="w-3.5 h-3.5" />
        {label}
      </button>
    )
  }

  const href = (kind === 'mail' ? 'mailto:' : 'tel:') + value
  return (
    <a
      href={href}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      className="flex items-center gap-1.5 text-[11px] font-bold text-[var(--accent)] transition-colors break-all"
    >
      <Icon className="w-3.5 h-3.5 shrink-0" />
      <span className="truncate">{value}</span>
    </a>
  )
}
